import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { BellIcon } from '@chakra-ui/icons';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';
import { useUIStore } from '#store';

const ADMIN_DASH_PATH = /^\/admin\/dashboard\/?$/;

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function AdminNotificationsDrawer() {
  const location = useLocation();
  const { user } = useUIStore((s) => ({ user: s.user }), shallow);
  const { isOpen, onClose, onToggle } = useDisclosure();

  const isAdminRoute =
    user?.role === 'admin' && location.pathname.startsWith('/admin');
  const isAdminDashboard = ADMIN_DASH_PATH.test(location.pathname);

  const { loading, notifications, unreadCount, load, onMarkRead, onMarkAllRead } =
    useAdminNotifications({
      enabled: isAdminRoute && !isAdminDashboard,
    });

  useEffect(() => {
    if (!isAdminRoute) {
      onClose();
    }
  }, [isAdminRoute, onClose]);

  useEffect(() => {
    if (isOpen && isAdminRoute && !isAdminDashboard) {
      void load();
    }
  }, [isOpen, isAdminRoute, isAdminDashboard, load]);

  const onMarkAllReadAndClose = async () => {
    await onMarkAllRead();
    onClose();
  };

  if (!isAdminRoute || isAdminDashboard) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label="Admin alerts"
        icon={
          <Box position="relative">
            <BellIcon boxSize={5} />
            {unreadCount > 0 ? (
              <Badge
                position="absolute"
                top="-6px"
                right="-8px"
                colorScheme="red"
                borderRadius="full"
                fontSize="0.65rem"
                minW="1.1rem"
                textAlign="center"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            ) : null}
          </Box>
        }
        position="fixed"
        top={{ base: '72px', md: '80px' }}
        right={4}
        zIndex={1400}
        colorScheme="brand"
        variant="solid"
        size="md"
        onClick={onToggle}
      />

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent maxW={{ base: '100%', md: '420px' }}>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex align="center" justify="space-between" pr={10} gap={2} wrap="wrap">
              <Text fontSize="lg" fontWeight="bold">
                Alerts
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void onMarkAllReadAndClose()}
                isDisabled={unreadCount === 0}
              >
                Mark all read
              </Button>
            </Flex>
          </DrawerHeader>
          <DrawerBody px={3}>
            {loading ? (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            ) : notifications.length === 0 ? (
              <Text color="gray.500" py={6}>
                No notifications yet.
              </Text>
            ) : (
              <Flex direction="column" gap={3} pb={6}>
                {notifications.map((n) => (
                  <Box
                    key={n._id}
                    p={3}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={n.read ? 'gray.200' : 'brand.200'}
                    bg={n.read ? 'gray.50' : 'brand.50'}
                  >
                    <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
                      <Text fontSize="xs" color="gray.500">
                        {formatTime(n.createdAt)}
                      </Text>
                      {!n.read ? (
                        <Button size="xs" variant="ghost" onClick={() => void onMarkRead(n._id)}>
                          Mark read
                        </Button>
                      ) : null}
                    </Flex>
                    <Text fontSize="sm" fontWeight={n.read ? 'normal' : 'semibold'} whiteSpace="pre-wrap">
                      {n.message}
                    </Text>
                    {n.kind ? (
                      <Badge mt={2} fontSize="0.65rem" colorScheme="purple">
                        {n.kind}
                      </Badge>
                    ) : null}
                  </Box>
                ))}
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
